import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { logClientErrorToStorage } from './utils/errorLogger';

// Solución para errores de DOM y recursión - versión ligera que no modifica prototipos nativos
if (typeof window !== 'undefined') {
  // Contador de profundidad para detectar posible recursión excesiva
  window.__recursionCounter = {};

  // Detector de recursión excesiva
  window.__checkRecursion = function(functionName) {
    if (!window.__recursionCounter) window.__recursionCounter = {};
    window.__recursionCounter[functionName] = (window.__recursionCounter[functionName] || 0) + 1;

    // Si detectamos más de 100 llamadas al mismo método en un ciclo, podemos estar en recursión infinita
    if (window.__recursionCounter[functionName] > 100) {
      console.warn(`Posible recursión infinita detectada en: ${functionName}`);
      // Reset para que no siga creciendo indefinidamente
      setTimeout(() => { window.__recursionCounter = {}; }, 0);
      return true;
    }

    // Reset automático cada 50ms (suficiente para detectar recursión pero no interfiere con uso normal)
    if (window.__recursionCounter[functionName] === 1) {
      setTimeout(() => {
        if (window.__recursionCounter && window.__recursionCounter[functionName]) {
          delete window.__recursionCounter[functionName];
        }
      }, 50);
    }

    return false;
  };
}

// Registrar errores globales del navegador
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    try {
      const err = event?.error || new Error(event?.message || 'window.onerror');

      // Detectar y manejar específicamente errores de recursión
      if (err.name === 'RangeError' && err.message.includes('Maximum call stack size exceeded')) {
        console.warn('Detectado error de recursión infinita, intentando recuperar la aplicación...');

        // Limpiar contadores de recursión
        window.__recursionCounter = {};

        // Intentar recuperar estado global que podría causar loops
        try {
          if (window.__inPopStateHandler) window.__inPopStateHandler = false;
        } catch (e) {}
      }

      // Continuar con el registro normal del error
      logClientErrorToStorage(err, { componentStack: null }, {
        source: 'window.onerror',
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
      }).catch(() => {});
    } catch (_) {}
  });

  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event?.reason;
      const err = reason instanceof Error ? reason : new Error(String(reason));
      logClientErrorToStorage(err, { componentStack: null }, {
        source: 'unhandledrejection',
      }).catch(() => {});
    } catch (_) {}
  });
}

// Parche defensivo contra errores de mutación del DOM (evita NotFoundError por desincronización de terceros/portales)
// Solo se activa en producción salvo que se deshabilite explícitamente con REACT_APP_DOM_GUARD=off
if (typeof window !== 'undefined' && typeof Node !== 'undefined') {
  try {
    const isProd = process.env.NODE_ENV === 'production';
    const ENABLE_DOM_GUARD = process.env.REACT_APP_DOM_GUARD !== 'off';
    if (ENABLE_DOM_GUARD && isProd) {
      const original = {
        removeChild: Node.prototype.removeChild,
        insertBefore: Node.prototype.insertBefore,
        replaceChild: Node.prototype.replaceChild,
        appendChild: Node.prototype.appendChild,
      };

      const logOnce = (info) => {
        try {
          window.__DOM_PATCH_LOGS = window.__DOM_PATCH_LOGS || [];
          // Limitar a 20 entradas
          if (window.__DOM_PATCH_LOGS.length > 20) window.__DOM_PATCH_LOGS.shift();
          window.__DOM_PATCH_LOGS.push({ t: Date.now(), ...info });
        } catch (_) {}
      };

      Node.prototype.removeChild = function(child) {
        try {
          if (!child || child.parentNode !== this) {
            // No corresponde a este padre: ignorar de forma segura
            logOnce({ kind: 'removeChild_ignored', reason: 'not_a_child', tag: this?.nodeName, childTag: child?.nodeName });
            return child;
          }
          return original.removeChild.call(this, child);
        } catch (e) {
          if (e && (e.name === 'NotFoundError' || e.message?.includes('NotFoundError'))) {
            logOnce({ kind: 'removeChild_caught', message: e.message });
            // Intentar una comprobación adicional: si el nodo ya no está en el documento, ignorar
            if (!child?.isConnected) return child;
            // Como fallback, no lanzar para evitar romper la UI
            return child;
          }
          throw e;
        }
      };

      Node.prototype.insertBefore = function(newNode, referenceNode) {
        try {
          if (!referenceNode || referenceNode.parentNode !== this) {
            // Fallback a appendChild si la referencia no pertenece a este padre
            logOnce({ kind: 'insertBefore_fallback_append', tag: this?.nodeName });
            return original.appendChild.call(this, newNode);
          }
          return original.insertBefore.call(this, newNode, referenceNode);
        } catch (e) {
          if (e && (e.name === 'NotFoundError' || e.message?.includes('NotFoundError'))) {
            logOnce({ kind: 'insertBefore_caught', message: e.message });
            // Fallback seguro
            return original.appendChild.call(this, newNode);
          }
          throw e;
        }
      };

      Node.prototype.replaceChild = function(newChild, oldChild) {
        try {
          if (newChild === oldChild) return oldChild;
          if (!oldChild || oldChild.parentNode !== this) {
            // Si el viejo hijo no pertenece, solo anexar el nuevo
            logOnce({ kind: 'replaceChild_fallback_append', tag: this?.nodeName });
            return original.appendChild.call(this, newChild);
          }
          return original.replaceChild.call(this, newChild, oldChild);
        } catch (e) {
          if (e && (e.name === 'NotFoundError' || e.message?.includes('NotFoundError'))) {
            logOnce({ kind: 'replaceChild_caught', message: e.message });
            // Fallback seguro
            try { return original.appendChild.call(this, newChild); } catch (_) { return oldChild; }
          }
          throw e;
        }
      };

      Node.prototype.appendChild = function(child) {
        try {
          return original.appendChild.call(this, child);
        } catch (e) {
          if (e && (e.name === 'NotFoundError' || e.message?.includes('NotFoundError'))) {
            logOnce({ kind: 'appendChild_caught', message: e.message });
            return child;
          }
          throw e;
        }
      };

      // Señal para el logger
      try { window.__DOM_GUARD_ACTIVE = true; } catch (_) {}
    }
  } catch (e) {
    // No romper la app si el parche falla
    console.warn('No se pudo aplicar el DOM guard:', e?.message || e);
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  //<React.StrictMode>
    <App />
  //</React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
